import React, { useCallback, useContext, useEffect } from "react";
import { OmniboxSearchResult } from "common-client/generated/graphql";
import { useDebouncedCallback } from "common-client/utils/useDebouncedCallback";
import { useExecuteSearchLazyQuery } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { FlexColumn } from "../Common/__styles__/Layout";
import Divider from "../Common/Divider";
import { Icon } from "../Common/Icons/LucideIcons";
import { Text } from "../Inputs";
import { InternalMapContext } from "../Maps/InternalMapContextProvider";
import { LayerContext } from "../Maps/layers";
import { SearchResultProps } from ".";
import {
  LeftContentWrapper,
  OmniboxSearchContainer,
  OmniboxSearchInputWrapper,
} from "./__styles__/OmniboxSearch";
import { OmniboxResults } from "./OmniboxResults";
import { useRecentSearches } from "./useRecentSearches";
import { searchResultCallbackProps } from "./utils";

const OmniboxSearchInput = () => {
  const { searchState, searchStateDispatch } = useContext(LayerContext);

  const [executeSearch] = useExecuteSearchLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: ({ search }) =>
      searchStateDispatch({
        type: "searchComplete",
        results: search,
      }),
  });

  const debouncedExecuteSearch = useDebouncedCallback(async (value: string) => {
    await executeSearch({ variables: { query: value } });
  }, 250);

  return (
    <OmniboxSearchInputWrapper>
      <LeftContentWrapper>
        <Icon iconName="search" color="contentSecondary" size="16" />
        <Text
          name="omnibox-search-input"
          data-testid="omnibox-search-input"
          placeholder="Search..."
          onChange={async value => {
            if (value) {
              searchStateDispatch({ type: "searchStarted", query: value });
              await debouncedExecuteSearch(value);
            } else {
              searchStateDispatch({ type: "searchCleared" });
            }
          }}
          onFocus={() =>
            searchStateDispatch({
              type: "setSearchPanel",
              searchPanelOpen: true,
            })
          }
          size="small"
          value={searchState.query ?? ""}
          maxLength={256}
          style={{
            padding: 0,
            border: "none",
            height: "24px",
          }}
        />
      </LeftContentWrapper>
      {(searchState.query || searchState.searchPanelOpen) && (
        <Icon
          iconName="x"
          color="contentSecondary"
          size="16"
          data-testid="omnibox-search-clear"
          style={{ cursor: "pointer" }}
          onClick={() => {
            searchStateDispatch({ type: "resetSearch" });
          }}
        />
      )}
    </OmniboxSearchInputWrapper>
  );
};

export const OmniboxSearch = ({
  handleSearchResultClick,
  halfMap,
}: {
  halfMap: boolean;
  handleSearchResultClick: (searchResult: SearchResultProps) => void;
}) => {
  const { searchState, searchStateDispatch } = useContext(LayerContext);
  const { addRecentSearch } = useRecentSearches();

  // needed while we still have the old search around
  const { setResetSearchCategory } = useContext(InternalMapContext);
  useEffect(() => {
    if (setResetSearchCategory) {
      setResetSearchCategory(() => {
        searchStateDispatch({ type: "resetSearch" });
      });
    }
  }, [setResetSearchCategory, searchStateDispatch]);

  const wrappedHandleSearchResultClick = useCallback(
    (result: OmniboxSearchResult) => {
      searchStateDispatch({ type: "resetSearch" });
      addRecentSearch(searchState.query ?? "", result);
      handleSearchResultClick(searchResultCallbackProps(result));
    },
    [
      handleSearchResultClick,
      searchStateDispatch,
      addRecentSearch,
      searchState.query,
    ],
  );

  return (
    <OmniboxSearchContainer>
      <FlexColumn>
        <OmniboxSearchInput />
        {searchState.searchPanelOpen &&
          (halfMap ?
            <Divider margins={`0 -${spacing.s.value}`} />
          : <Divider extension={16} />)}
      </FlexColumn>
      {searchState.searchPanelOpen && searchState.query && (
        <OmniboxResults
          handleSearchResultClick={wrappedHandleSearchResultClick}
        />
      )}
    </OmniboxSearchContainer>
  );
};
